import { redirect, useNavigate } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import type { CreditCard } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import type { GetCreditCardsResponse } from "schema/gen/es/chiikipoint/wallet/v2/service_pb";
import { css } from "../../../../styled-system/css";
import Header from "../../../components/header";
import {
  AddCreditCardButton,
  CreditCardManageRow,
  creditCardRegistrationLimit,
} from "../../../features/charge/card-ui";
import { getCreditCardsOrEmpty } from "../../../features/charge/credit-card-loader";
import { pageCss } from "../../../features/charge/styles";
import { t } from "../../../libs/i18n";

type ChargeSearch = {
  amount?: number;
  // 遷移元。設定画面から来た場合に戻り先を設定画面へ切り替えるために保持する
  from?: "settings";
};

type LoaderData = {
  response: GetCreditCardsResponse;
};

export const Route = createFileRoute("/_authed/charge/credit-card/manage")({
  validateSearch: (search: Record<string, unknown>): ChargeSearch => ({
    amount: Number(search.amount) === 10000 ? 10000 : 5000,
    from: search.from === "settings" ? "settings" : undefined,
  }),
  beforeLoad: ({ context: { tenant } }) => {
    if (!tenant.feature.charge) {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context: { client } }) =>
    ({
      response: (await getCreditCardsOrEmpty(client)).response,
    }) satisfies LoaderData,
  component: CreditCardManagement,
});

function CreditCardManagement() {
  const { response } = Route.useLoaderData();
  const { amount = 5000, from } = Route.useSearch();
  const navigate = useNavigate();
  const cards = useMemo(() => Object.values(response.cards), [response.cards]);
  const defaultCardId = response.defaultGmoCardId;
  const reachedCreditCardLimit = cards.length >= creditCardRegistrationLimit;

  return (
    <>
      <Header
        title={t("クレジットカード管理")}
        onClickBack={() =>
          from === "settings"
            ? navigate({ to: "/settings" })
            : navigate({ to: "/charge/credit", search: { amount } })
        }
      />
      <main className={pageCss}>
        <section
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          })}
        >
          <h2
            className={css({
              fontSize: "14px",
              lineHeight: "1.4",
              fontWeight: "600",
              color: "text.tertiary",
            })}
          >
            {t("登録済みクレジットカード")}
          </h2>
          <div
            className={css({
              bg: "surface.primary",
              border: "1px solid",
              borderColor: "border.secondary",
              rounded: "16px",
              overflow: "hidden",
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.03)",
            })}
          >
            {cards.length > 0 ? (
              cards.map((card: CreditCard, index) => (
                <CreditCardManageRow
                  key={card.gmoCardId}
                  card={card}
                  isDefault={defaultCardId === card.gmoCardId}
                  showDivider={index < cards.length - 1}
                  onClick={() =>
                    navigate({
                      to: "/charge/credit-card/manage/$gmoCardId",
                      params: { gmoCardId: card.gmoCardId },
                      search: { amount, from },
                    })
                  }
                />
              ))
            ) : (
              <p
                className={css({
                  px: "20px",
                  py: "24px",
                  color: "text.secondary",
                  fontSize: "14px",
                  lineHeight: "1.4",
                })}
              >
                {t("登録済みのクレジットカードはありません。")}
              </p>
            )}
          </div>
        </section>
        <AddCreditCardButton
          disabled={reachedCreditCardLimit}
          onClick={() =>
            navigate({ to: "/charge/credit-card/add", search: { amount } })
          }
        />
      </main>
    </>
  );
}
